import { useEffect } from "react";

import useSystemStore from "@store/useSystemStore";
import maintenanceService from "@services/maintenance.service";
import { ApiError, addResponseInterceptor, isServiceUnavailable } from "@config/apiClient";
import { MAINTENANCE_503_STREAK_THRESHOLD, MAINTENANCE_RECOVERY_POLL_MS } from "@config/constants";
import { parseMaintenanceWindow } from "@utils/maintenance.util";

import { useMaintenanceStatus } from "./useMaintenanceStatus";

interface UseMaintenanceResult {
  isChecking: boolean;
}

/**
 * Decides whether the app is behind the maintenance page (OUT-440). The tools service raises
 * and lowers it; `503`s raise it as a fallback for an outage the flag hasn't caught up with.
 *
 * Only the backend probe lowers a `503`-raised maintenance: while the page is up the app tree
 * is unmounted, so the only successes that could still reach the interceptor are requests
 * already in flight when the outage started — clearing on those would flap the screen.
 */
export function useMaintenance(): UseMaintenanceResult {
  const { status, isChecking } = useMaintenanceStatus();
  const isMaintenance = useSystemStore((state) => state.isMaintenance);
  const maintenanceSource = useSystemStore((state) => state.maintenanceSource);

  useEffect(() => {
    if (status) useSystemStore.getState().applyMaintenanceStatus(status);
  }, [status]);

  useEffect(() => {
    // A 503 whose body carries a maintenance window is the backend's own maintenance response
    // and flips immediately. A bare 503 could equally be a rate limiter or one unhealthy
    // upstream behind the load balancer, so it only flips after a streak of them.
    let bare503Streak = 0;

    const eject = addResponseInterceptor(
      (response) => {
        bare503Streak = 0;
        return response;
      },
      async (error) => {
        if (isServiceUnavailable(error)) {
          const window = parseMaintenanceWindow((error as ApiError).response?.data);
          bare503Streak += 1;
          if (window || bare503Streak >= MAINTENANCE_503_STREAK_THRESHOLD) {
            useSystemStore.getState().setMaintenanceDetected(window);
          }
        } else {
          bare503Streak = 0;
        }
        return Promise.reject(error);
      }
    );

    return () => eject();
  }, []);

  useEffect(() => {
    if (!isMaintenance || maintenanceSource !== "response") return;

    let cancelled = false;
    const probe = async () => {
      const reachable = await maintenanceService.isBackendReachable();
      if (!cancelled && reachable) {
        useSystemStore.getState().clearMaintenance();
      }
    };

    const intervalId = setInterval(probe, MAINTENANCE_RECOVERY_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [isMaintenance, maintenanceSource]);

  return { isChecking: isChecking && !isMaintenance };
}
